// <s-header-tone> — the ALBRÄ sticky header's behaviour layer.
// 1. Tone: adds `zone-ink` while the bar overlaps a [data-header-tone="ink"] frame; white everywhere else.
// 2. Divider: sets [data-scrolled] once the page leaves the top, so the 1px divider appears only when needed.
// 3. Mega-menu (desktop): publishes --albra-header-bottom for the popover's top edge, opens the
//    #albra-menu popover on hover/focus intent over a world in row 2, marks the active world, and
//    closes after the pointer leaves header and panel. Escape and click-outside come from popover="auto".
const OPEN_DELAY = 140;
const CLOSE_DELAY = 220;

class HeaderTone extends HTMLElement {
  #observer = null;
  #active = new Set();
  #controller = null;
  #menu = null;
  #openTimer = 0;
  #closeTimer = 0;
  #finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  #desktop = window.matchMedia('(min-width: 64rem)');

  connectedCallback() {
    this.#controller = new AbortController();
    const signal = this.#controller.signal;

    this.#observeTone();
    this.#syncScrolled();
    this.#publishBottom();

    window.addEventListener('scroll', () => this.#syncScrolled(), { signal, passive: true });
    window.addEventListener('resize', () => { this.#observeTone(); this.#publishBottom(); }, { signal, passive: true });
    new ResizeObserver(() => this.#publishBottom()).observe(this);

    this.#menu = document.getElementById('albra-menu');
    if (!this.#menu) return;

    this.#menu.addEventListener('toggle', (event) => {
      const open = event.newState === 'open';
      this.toggleAttribute('data-menu-open', open);
      this.querySelector('[data-menu-toggle]')?.setAttribute('aria-expanded', String(open));
      // The open panel is white, so the bar above it turns white too; tone resumes on close.
      if (open) { this.#publishBottom(); this.classList.remove('zone-ink'); }
      else { this.#setWorld(''); this.#observeTone(); }
    }, { signal });

    for (const trigger of this.querySelectorAll('[data-world-trigger]')) {
      const world = trigger.dataset.worldTrigger;
      trigger.addEventListener('pointerenter', () => {
        if (!this.#finePointer.matches || !this.#desktop.matches) return;
        this.#scheduleOpen(world);
      }, { signal });
      trigger.addEventListener('pointerleave', () => clearTimeout(this.#openTimer), { signal });
      // Keyboard users preview the world they focus while the menu is already open; Tab continues through the row.
      trigger.addEventListener('focusin', () => {
        if (this.#isOpen()) this.#setWorld(world);
      }, { signal });
    }

    // Leaving both the header and the panel closes a hover-opened menu; re-entering either cancels it.
    for (const zone of [this, this.#menu]) {
      zone.addEventListener('pointerleave', (event) => {
        if (!this.#finePointer.matches || event.pointerType !== 'mouse') return;
        if (this.#menu.dataset.openedBy !== 'hover') return;
        this.#closeTimer = setTimeout(() => this.#hide(), CLOSE_DELAY);
      }, { signal });
      zone.addEventListener('pointerenter', () => clearTimeout(this.#closeTimer), { signal });
    }

    // A click on the Menu button is an explicit open: it should not auto-close on pointer leave.
    this.querySelector('[data-menu-toggle]')?.addEventListener('click', (event) => {
      if (this.#isOpen() && this.#menu.dataset.openedBy === 'hover') {
        // Promote a hover preview to an explicit open instead of toggling it shut.
        event.preventDefault();
        this.#menu.dataset.openedBy = 'click';
        return;
      }
      this.#menu.dataset.openedBy = 'click';
      this.#setWorld('');
    }, { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    clearTimeout(this.#openTimer);
    clearTimeout(this.#closeTimer);
  }

  #isOpen() {
    return this.#menu?.matches(':popover-open') ?? false;
  }

  #scheduleOpen(world) {
    clearTimeout(this.#closeTimer);
    clearTimeout(this.#openTimer);
    if (this.#isOpen()) {
      this.#setWorld(world);
      return;
    }
    this.#openTimer = setTimeout(() => {
      this.#menu.dataset.openedBy = 'hover';
      this.#menu.showPopover();
      this.#setWorld(world);
    }, OPEN_DELAY);
  }

  #hide() {
    if (this.#isOpen()) this.#menu.hidePopover();
  }

  #setWorld(world) {
    if (!this.#menu) return;
    this.#menu.dataset.activeWorld = world;
    for (const trigger of this.querySelectorAll('[data-world-trigger]')) {
      const link = trigger.querySelector('a');
      if (!link) continue;
      if (world && trigger.dataset.worldTrigger === world) link.setAttribute('aria-expanded', 'true');
      else link.removeAttribute('aria-expanded');
    }
  }

  #syncScrolled() {
    this.toggleAttribute('data-scrolled', window.scrollY > 4);
  }

  #publishBottom() {
    const bottom = Math.round(this.getBoundingClientRect().bottom);
    if (bottom > 0) document.documentElement.style.setProperty('--albra-header-bottom', `${bottom}px`);
  }

  #observeTone() {
    this.#observer?.disconnect();
    this.#active.clear();
    const frames = document.querySelectorAll('[data-header-tone="ink"]');
    if (!frames.length) {
      this.classList.remove('zone-ink');
      return;
    }
    // Watch a thin band just below the header's bottom edge.
    const band = this.offsetHeight + 8;
    const bottom = Math.max(0, window.innerHeight - band);
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.#active.add(entry.target);
          else this.#active.delete(entry.target);
        }
        this.classList.toggle('zone-ink', this.#active.size > 0 && !this.#isOpen());
      },
      { rootMargin: `0px 0px -${bottom}px 0px` },
    );
    frames.forEach((frame) => this.#observer.observe(frame));
  }
}

if (!customElements.get('s-header-tone')) customElements.define('s-header-tone', HeaderTone);
